import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_KPI_MAP,
  EMPLOYEE_STATUS_MAP,
  KPI_GRADE_DEFS,
  REVIEW_VERDICT_MAP,
  REWORK_CAUSE_DEFS,
  REWORK_CAUSE_MAP,
  type AgencyKpiCard,
  type EmployeeStatus,
  type ReworkCause,
  type ReviewVerdict,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { AchieveRing, Fact, FactGrid } from '../../components/staff';
import { Avatar } from '../../components/People';
import { useToast } from '../../components/Toast';

/* ==========================================================================
   ملفُّ عضوٍ في فريق التنفيذ — كيف يُقاس، ومن أين جاء الرقم
   ========================================================================== */

/**
 * صفحةٌ لكلّ منفّذ.
 *
 * وفيها ثلاثةُ أسئلةٍ عن رجلٍ واحد: من هو، وأين يقف، ومن أين جاء رقمُه.
 * والثالثُ هو الفارق: رقمٌ لا يستطيع صاحبُه فحصَ مدخلاته لا يُدافَع عنه —
 * فمن كلّ بندٍ نزولٌ إلى تسليماته بأسمائها وتواريخها.
 *
 * ومن هنا تُراجَع التسليمات: المراجعةُ حكمٌ على عملٍ بعينه لا خلاصةٌ تُكتب
 * عن شخصٍ آخرَ الشهر.
 */

interface MemberData {
  windowDays: number;
  from: string;
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
    deptKey: string | null;
    email: string | null;
    phone: string | null;
    hireDate: string;
    status: EmployeeStatus;
    manager: { id: string; name: string } | null;
  } | null;
  card: AgencyKpiCard;
  deliveries: {
    id: string;
    title: string;
    projectId: string;
    projectName: string;
    projectColor: string;
    completedAt: string | null;
    dueAt: string | null;
    baselineDueAt: string | null;
    onTime: boolean | null;
    moved: boolean;
  }[];
  reviews: {
    id: string;
    taskId: string;
    taskTitle: string;
    verdict: ReviewVerdict;
    cause: ReworkCause | null;
    causeLabel: string | null;
    onExecutor: boolean;
    feedback: string | null;
    createdAt: string;
  }[];
}

const WINDOWS = [
  { days: 30, label: 'شهر' },
  { days: 90, label: 'ثلاثة أشهر' },
  { days: 180, label: 'ستة أشهر' },
];

export function AgencyTeamMember() {
  const { id = '' } = useParams();
  const [windowDays, setWindowDays] = useState(90);
  const [reviewing, setReviewing] = useState<{ id: string; title: string } | null>(null);

  const data = useQuery({
    queryKey: ['agency', 'kpi-member', id, windowDays],
    queryFn: () => api<MemberData>(`/agency/kpi/team/${id}${qs({ windowDays })}`),
    placeholderData: (prev) => prev,
  });

  const queue = useQuery({
    queryKey: ['agency', 'kpi-queue'],
    queryFn: () =>
      api<{ id: string; title: string; assigneeId: string; completedAt: string | null }[]>(
        '/agency/kpi/queue',
      ),
  });

  if (data.isError) {
    return (
      <div className="page">
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      </div>
    );
  }

  const d = data.data;
  const e = d?.employee;
  const card = d?.card;
  const grade = card?.grade ? KPI_GRADE_DEFS.find((g) => g.key === card.grade) : null;
  const mine = (queue.data ?? []).filter((t) => t.assigneeId === card?.userId);

  return (
    <div className="page">
      <PageHead
        title={e?.name ?? 'عضو الفريق'}
        subtitle={[e?.jobTitle, card?.deptLabel].filter(Boolean).join(' — ') || undefined}
        actions={
          <>
            <span className="seg" role="group" aria-label="نافذة القياس">
              {WINDOWS.map((w) => (
                <button
                  key={w.days}
                  type="button"
                  className={`seg-btn${windowDays === w.days ? ' is-on' : ''}`}
                  onClick={() => setWindowDays(w.days)}
                >
                  {w.label}
                </button>
              ))}
            </span>
            <Link to="/agency/team" className="btn">
              <Icon name="chevronRight" size={14} />
              الفريق
            </Link>
          </>
        }
      />

      {data.isPending && !d && <div className="skeleton" style={{ height: 260 }} />}

      {d && card && (
        <>
          {card.state !== 'MEASURED' && (
            <Notice
              kind={card.state === 'NO_ACCOUNT' ? 'warning' : 'info'}
              icon="info"
              title={
                card.state === 'NO_ACCOUNT'
                  ? 'لا يُقاس — بلا حساب مستخدم'
                  : card.state === 'NOT_MEASURED'
                    ? 'لا يُقاس ببنود التنفيذ'
                    : card.state === 'NEW_HIRE'
                      ? 'فترةُ تعرُّف'
                      : 'الدليلُ لا يكفي بعد'
              }
            >
              {card.stateReason}
            </Notice>
          )}

          <div className="cols-2 mb-4">
            {/* الملفّ الوظيفي */}
            <div className="card col-card">
              <div className="card-head">
                <h2 className="card-title">الملفّ الوظيفي</h2>
                {e && (
                  <Link to={`/hr/employees/${e.id}`} className="tiny">
                    ملفّه الكامل
                  </Link>
                )}
              </div>
              <div className="card-body col-body">
                <div className="row mb-4" style={{ gap: 10, alignItems: 'center' }}>
                  <Avatar name={card.name} src={card.avatarUrl} size={46} />
                  <div>
                    <strong>{card.name}</strong>
                    <div className="tiny mute">
                      {e?.status && EMPLOYEE_STATUS_MAP[e.status]?.label}
                    </div>
                  </div>
                </div>
                <FactGrid>
                  <Fact icon="briefcase" label="المسمّى" value={e?.jobTitle || '—'} />
                  <Fact icon="building" label="القسم" value={card.deptLabel || '—'} />
                  <Fact icon="clipboard" label="الكود" value={e?.code || '—'} ltr />
                  <Fact icon="calendar" label="الالتحاق" value={e ? date(e.hireDate) : '—'} />
                  <Fact icon="user" label="المدير" value={e?.manager?.name || '—'} />
                  <Fact icon="mail" label="البريد" value={e?.email || '—'} ltr />
                </FactGrid>
              </div>
            </div>

            {/* النقاط */}
            <div className="card col-card">
              <div className="card-head">
                <h2 className="card-title">النقاط في {windowDays} يومًا</h2>
                {card.trend != null && card.trend !== 0 && (
                  <span className={`tiny num${card.trend > 0 ? ' ag-up' : ' ag-late-text'}`}>
                    {card.trend > 0 ? `+${card.trend}` : card.trend} عن الفترة السابقة
                  </span>
                )}
              </div>
              <div className="card-body col-body">
                {/*
                  ولا قوسَ لمن لا درجةَ له.
                  قوسٌ عند الصفر يُقرأ «صفرٌ من مئة» — وهو حكمٌ على رجلٍ لم
                  يُقَس أصلًا. علامةُ الغائب فراغٌ مشروحٌ لا دائرةٌ فارغة.
                */}
                <div className="row" style={{ gap: 'var(--sp-4)', alignItems: 'center' }}>
                  {card.score != null && <AchieveRing pct={card.score} />}
                  <div>
                    {card.score == null ? (
                      <p className="mute small">{card.stateReason ?? 'لا قياس'}</p>
                    ) : (
                      <>
                        <Badge kind={grade?.kind}>{grade?.label}</Badge>
                        <p className="tiny mute mt-2">{grade?.hint}</p>
                        <p className="tiny mute">
                          بلوغُ كلّ الأهداف بالضبط يعطي ٨٠ — لا ١٠٠.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* البنود */}
          <div className="kpi-grid mb-4">
            {card.kpis.map((k) => {
              const def = AGENCY_KPI_MAP[k.key];
              const shown =
                k.value == null ? '—' : def.unit === 'PERCENT' ? `${k.value}٪` : `${k.value}`;
              return (
                <section key={k.key} className={`kpi-card${k.counted ? '' : ' is-off'}`}>
                  <div className="kpi-top">
                    <strong>{def.label}</strong>
                    <span className="ag-chip num">وزن {def.weight}٪</span>
                  </div>
                  <div className="kpi-value">
                    <b className="num">{shown}</b>
                    <span className="tiny mute num">
                      الهدف {def.unit === 'PERCENT' ? `${def.target}٪` : def.target}
                    </span>
                  </div>
                  {k.counted && k.score != null ? (
                    <div className="ag-progress" title={`${k.score} نقطة`}>
                      <span className="ag-progress-fill" style={{ width: `${k.score}%` }} />
                    </div>
                  ) : (
                    <p className="tiny mute">{k.reason}</p>
                  )}
                  <p className="tiny mute">{def.description}</p>
                  <span className="tiny mute num">{k.n} فرصة في هذه الفترة</span>
                </section>
              );
            })}
          </div>

          {/* ما يُقاس ولا يُحتسب */}
          <section className="card mb-4">
            <div className="card-head">
              <h2 className="card-title">ما يُقاس ولا يُحتسب</h2>
              <span className="tiny mute">سياقٌ يمنع قراءةَ الرقم على غير وجهه</span>
            </div>
            <div className="card-body">
              <div className="pf-stats">
                <span className="pf-stat">
                  <b className="num">{card.context.completed}</b>
                  <span className="tiny mute">سُلّمت</span>
                </span>
                <span className="pf-stat">
                  <b className="num">{card.context.assigned}</b>
                  <span className="tiny mute">أُسندت إليه</span>
                </span>
                <span className={`pf-stat${card.context.openOverdue > 0 ? ' is-late' : ''}`}>
                  <b className="num">{card.context.openOverdue}</b>
                  <span className="tiny mute">متأخّرة مفتوحة</span>
                </span>
                <span className={`pf-stat${card.context.undated > 0 ? ' is-warn' : ''}`}>
                  <b className="num">{card.context.undated}</b>
                  <span className="tiny mute">سُلّمت بلا موعد</span>
                </span>
              </div>

              {card.context.undated > 0 && (
                <p className="tiny mute mt-2">
                  المهمّةُ بلا موعدٍ لا تتأخّر أبدًا — فما سُلّم بلا موعدٍ يُعدّ هنا ولا يدخل
                  حساب الالتزام، لئلّا تُكافأ قلّةُ التأريخ.
                </p>
              )}

              {card.context.onTimeCurrent != null &&
                (() => {
                  const first = card.kpis.find((k) => k.key === 'ON_TIME')?.value;
                  if (first == null) return null;
                  const gap = Math.round((card.context.onTimeCurrent - first) * 10) / 10;
                  if (Math.abs(gap) < 3) return null;
                  return (
                    <Notice kind="warning" icon="clock" title="فجوةٌ بين الموعد الأوّل والحاليّ">
                      الالتزامُ على الموعد الأوّل <b className="num">{first}٪</b>، وعلى الموعد
                      الحاليّ <b className="num">{card.context.onTimeCurrent}٪</b> — الفارقُ{' '}
                      <b className="num">{gap > 0 ? `+${gap}` : gap}</b> نقطة. المواعيدُ تُحرَّك،
                      والقياسُ على الأوّل عمدًا: من قيس التزامَه بعمودٍ يملك تحريرَه قاس نفسَه
                      بنفسه.
                    </Notice>
                  );
                })()}
            </div>
          </section>

          {/* طابور المراجعة */}
          {mine.length > 0 && (
            <section className="card mb-4">
              <div className="card-head">
                <h2 className="card-title">
                  <Icon name="check" size={15} /> تسليماتٌ تنتظر مراجعتك
                </h2>
                <span className="tiny mute">النظامُ يختارها — أقدمُ ما لم يُراجَع</span>
              </div>
              <div className="card-body">
                <ul className="ag-task-list">
                  {mine.map((t) => (
                    <li key={t.id}>
                      <div className="ag-task-row">
                        <span className="ag-task-title">{t.title}</span>
                        {t.completedAt && (
                          <span className="tiny mute num">{date(t.completedAt)}</span>
                        )}
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setReviewing({ id: t.id, title: t.title })}
                        >
                          راجِع
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          <div className="ag-home-cols">
            {/* التسليمات — من أين جاء رقم الالتزام */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">تسليماته</h2>
                <span className="tiny mute">مصدرُ رقم الالتزام</span>
              </div>
              <div className="card-body">
                {d.deliveries.length === 0 ? (
                  <p className="mute small">لا تسليماتٍ في هذه الفترة.</p>
                ) : (
                  <ul className="ag-task-list">
                    {d.deliveries.slice(0, 20).map((t) => (
                      <li key={t.id}>
                        <Link
                          to={`/agency/projects/${t.projectId}`}
                          className="ag-task-row"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <span
                            className={`ag-dot`}
                            style={{
                              background:
                                t.onTime == null
                                  ? 'var(--line-2)'
                                  : t.onTime
                                    ? 'var(--success)'
                                    : 'var(--danger)',
                            }}
                          />
                          <span className="ag-task-title">{t.title}</span>
                          {t.moved && (
                            <span className="ag-chip" title="حُرّك موعدُها بعد تأريخها">
                              حُرّك الموعد
                            </span>
                          )}
                          {t.baselineDueAt == null && (
                            <span className="ag-chip">بلا موعد</span>
                          )}
                          <span className="tiny mute num">
                            {t.completedAt ? date(t.completedAt) : '—'}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* المراجعات — من أين جاء رقم الردّ */}
            <section className="card">
              <div className="card-head">
                <h2 className="card-title">مراجعاتُ تسليماته</h2>
                <span className="tiny mute">مصدرُ رقم الردّ للتعديل</span>
              </div>
              <div className="card-body">
                {d.reviews.length === 0 ? (
                  <p className="mute small">
                    لا مراجعاتٍ بعد — وبندُ «الردّ للتعديل» لا يُحتسب حتى تُراجَع تسليماتُه.
                  </p>
                ) : (
                  <ul className="ag-task-list">
                    {d.reviews.map((r) => (
                      <li key={r.id}>
                        <div className="ag-task-row">
                          <Badge kind={REVIEW_VERDICT_MAP[r.verdict]?.kind}>
                            {REVIEW_VERDICT_MAP[r.verdict]?.label}
                          </Badge>
                          <span className="ag-task-title">{r.taskTitle}</span>
                          {r.causeLabel && (
                            <span
                              className={`ag-chip${r.onExecutor ? ' ag-chip-late' : ''}`}
                              title={
                                r.onExecutor
                                  ? 'يُحتسب على المنفّذ'
                                  : 'يُسجَّل ولا يُحتسب على المنفّذ'
                              }
                            >
                              {r.causeLabel}
                            </span>
                          )}
                          <span className="tiny mute num">{date(r.createdAt)}</span>
                        </div>
                        {r.feedback && <p className="tiny mute">{r.feedback}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
        </>
      )}

      {reviewing && (
        <ReviewModal
          taskId={reviewing.id}
          title={reviewing.title}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  );
}

/**
 * نافذةُ المراجعة.
 *
 * السببُ مطلوبٌ عند الردّ لا اختياريّ — وهو أداةُ الإنصاف كلُّها: ما رُدَّ
 * لتغيُّر طلبِ العميل أو لنقص البريف ليس عيبَ المنفّذ، ويُسجَّل ولا يُحتسب
 * عليه.
 */
function ReviewModal({
  taskId,
  title,
  onClose,
}: {
  taskId: string;
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [verdict, setVerdict] = useState<ReviewVerdict>('APPROVED');
  const [cause, setCause] = useState<ReworkCause>('EXECUTION');
  const [feedback, setFeedback] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api(`/agency/kpi/review/${taskId}`, {
        method: 'POST',
        body: {
          verdict,
          cause: verdict === 'REWORK' ? cause : null,
          feedback: feedback.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency'] });
      toast('سُجّلت المراجعة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت المراجعة.'),
  });

  return (
    <Modal title="مراجعة تسليم" onClose={onClose}>
      <p className="strong mb-4">{title}</p>

      <div className="field">
        <span className="label">الحكم</span>
        <div className="ag-kind-grid">
          {(['APPROVED', 'REWORK'] as ReviewVerdict[]).map((v) => (
            <button
              key={v}
              type="button"
              className={`ag-kind-card${verdict === v ? ' is-active' : ''}`}
              onClick={() => setVerdict(v)}
              aria-pressed={verdict === v}
            >
              <strong>
                <Badge kind={REVIEW_VERDICT_MAP[v].kind}>{REVIEW_VERDICT_MAP[v].label}</Badge>
              </strong>
            </button>
          ))}
        </div>
      </div>

      {verdict === 'REWORK' && (
        <div className="field">
          <span className="label">سببُ الردّ</span>
          {REWORK_CAUSE_DEFS.map((c) => (
            <label key={c.key} className="tpl-pick-btn" style={{ marginBottom: 6 }}>
              <input
                type="radio"
                name="cause"
                checked={cause === c.key}
                onChange={() => setCause(c.key)}
              />
              <span className="tpl-pick-body">
                <b>{c.label}</b>
                <span className="tiny mute">{c.hint}</span>
              </span>
            </label>
          ))}
          <span className="tiny mute">
            {REWORK_CAUSE_MAP[cause].onExecutor
              ? 'يُحتسب على المنفّذ في بند «الردّ للتعديل».'
              : 'يُسجَّل ويُعرض ولا يُحتسب على المنفّذ.'}
          </span>
        </div>
      )}

      <label className="field">
        <span className="label">ملاحظة (اختياري)</span>
        <textarea
          className="textarea"
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="ما الذي يُصلَح تحديدًا؟"
        />
      </label>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'جارٍ الحفظ…' : 'سجّل المراجعة'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
